// frontend/src/pages/MyOrders.jsx
import React, { useEffect, useState, useContext } from "react";
import axios from "axios";
import "./MyOrders.css";
import { AuthContext } from "../context/AuthContext";
import { authHeaders } from "../utils/authHeaders";

const API = process.env.REACT_APP_API_URL || "/api";

export default function MyOrders() {
  const { user } = useContext(AuthContext) || {};
  const providerId = user?.id;
  const role = String(user?.role || "").toLowerCase();
  const isAdmin = role === "admin";
  const isFieldTeam = role === "field team";
  const isScopedViewer = isAdmin || isFieldTeam;

  const [rows, setRows] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);

  const statusLabel = {
    pending: "Pending",
    accepted: "Diterima",
    rejected: "Ditolak",
    completed: "Selesai",
  };
  const allowed = new Set(["pending", "accepted", "rejected", "completed"]);
  const normStatus = (v) => {
    const s = String(v ?? "").trim().toLowerCase();
    return allowed.has(s) ? s : "pending";
  };
  const money = (value) => new Intl.NumberFormat("id-ID", {
    style: "currency",
    currency: "IDR",
    maximumFractionDigits: 0,
  }).format(Number(value) || 0);

  async function load() {
    if (!providerId) return;
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/business/orders`, {
        params: {
          ...(isScopedViewer
            ? { scope: isAdmin ? "all" : "institution" }
            : { user_id: providerId }),
          status: statusFilter || undefined,
          limit: 200,
        },
        headers: authHeaders(user),
      });
      const items = (data?.data || []).map((r) => ({
        ...r,
        status: normStatus(r.status),
      }));
      setRows(items);
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.message || "Gagal memuat order.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [providerId, statusFilter, role]);

  async function updateStatus(id, status) {
    try {
      await axios.put(`${API}/business/orders/${id}/status`, {
        status,
        user_id: providerId,
      }, {
        headers: authHeaders(user),
      });
      await load();
    } catch (e) {
      console.error(e);
      alert("Gagal memperbarui status.");
    }
  }

  return (
    <div className="orders-wrap">
      <div className="orders-toolbar">
        <div>
          <h2>{isScopedViewer ? "Pesanan Sahabat Kakao" : "Daftar Orderan"}</h2>
          {isScopedViewer && (
            <div className="muted">
              {isAdmin ? "Semua institusi" : `Institusi: ${user?.institution || "sesuai akun"}`}
            </div>
          )}
        </div>
        <div className="spacer" />
        <select
          className="orders-filter"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="">Semua Status</option>
          <option value="pending">Pending</option>
          <option value="accepted">Diterima</option>
          <option value="rejected">Ditolak</option>
          <option value="completed">Selesai</option>
        </select>
        <button className="btn" onClick={load} disabled={loading}>
          {loading ? "Memuat..." : "Refresh"}
        </button>
      </div>

      <div className="orders-table-wrap">
        <table className="orders-table">
          <thead>
            <tr>
              <th>Waktu</th>
              {isScopedViewer && <th>Sahabat Kakao</th>}
              <th>Pelanggan</th>
              <th>Kontak</th>
              <th>Layanan / Produk</th>
              <th>Qty</th>
              <th>Kupon</th>
              <th>Alamat</th>
              <th>Status</th>
              <th style={{ minWidth: 220 }}>Aksi</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={isScopedViewer ? 10 : 9} style={{ textAlign: "center", padding: 24 }}>
                  Belum ada data.
                </td>
              </tr>
            )}
            {rows.map((r) => {
              const s = normStatus(r.status);
              return (
                <tr key={r.id}>
                  <td>{new Date(r.created_at).toLocaleString()}</td>
                  {isScopedViewer && (
                    <td>
                      <strong>{r.provider_name || "-"}</strong>
                      <div className="muted">{r.provider_institution || "-"}</div>
                    </td>
                  )}
                  <td>{r.buyer_name}</td>
                  <td>
                    <a
                      href={`https://wa.me/${String(r.buyer_phone).replace(/\D/g, "")}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      {r.buyer_phone}
                    </a>
                  </td>
                  <td>
                    <div className="muted">{r.category}</div>
                    <div>{r.title}</div>
                  </td>
                  <td>{r.quantity}</td>
                  <td>
                    {r.coupon_code ? (
                      <>
                        <strong>{r.coupon_code}</strong>
                        <div className="muted">Potongan {money(r.coupon_discount)}</div>
                        {r.total_price != null && <div>Total {money(r.total_price)}</div>}
                      </>
                    ) : "-"}
                  </td>
                  <td>
                    {r.buyer_desa || "-"}, {r.buyer_kecamatan || "-"},{" "}
                    {r.buyer_kabupaten || "-"}
                  </td>
                  <td>
                    <span className={`o-badge o-badge-${s}`}>
                      {statusLabel[s]}
                    </span>
                  </td>
                  <td>
                    <div className="actions">
                      {!isScopedViewer && s === "pending" && (
                        <>
                          <button
                            className="btn success"
                            onClick={() => updateStatus(r.id, "accepted")}
                          >
                            Terima
                          </button>
                          <button
                            className="btn danger"
                            onClick={() => updateStatus(r.id, "rejected")}
                          >
                            Tolak
                          </button>
                        </>
                      )}
                      {!isScopedViewer && s === "accepted" && (
                        <>
                          <button
                            className="btn primary"
                            onClick={() => updateStatus(r.id, "completed")}
                          >
                            Selesai
                          </button>
                          <button
                            className="btn danger"
                            onClick={() => updateStatus(r.id, "rejected")}
                          >
                            Batalkan
                          </button>
                        </>
                      )}
                      {(isScopedViewer || s === "rejected" || s === "completed") && (
                        <span className="muted">—</span>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
